/**
 * Service layer — Priority Engine curated inputs (docs/PRIORITY_ENGINE.md, Phase 1a).
 *
 * All access to `priority_scores` / `priority_project_dials` goes through /api/priority (Clerk-authed,
 * manager-and-up). Those tables are RLS deny-all, so there is deliberately no browser-side Supabase
 * path — same shape as services/flights.js and services/threadHill.js.
 *
 * NOTHING CONSUMES THESE SCORES YET. Phase 2 builds the ranking in shadow mode; Phase 4 is the first
 * time the Flight Plan orders by it. This layer exists so curation (Phase 3) can start in parallel.
 */

/*
 * The scales, the weights, and the resolution rules all live in `api/_priorityModel.js` — ONE
 * implementation, imported by both this service and the server route that computes the shadow
 * ranking. Re-exported here so components keep importing them from the service layer and never
 * reach across into api/ themselves.
 *
 * Deliberately NOT the `lib/issuanceLayout.js` + `api/_issuanceLayout.js` mirror-a-copy pattern:
 * a layout constant drifting between two copies is a cosmetic bug; a scoring formula drifting
 * between the surface that shows it and the surface that computes it is a credibility failure.
 */
export {
  FIBONACCI,
  COST_OF_DELAY_SCALE,
  EFFORT_SCALE,
  PROJECT_DIALS,
  PRIORITY_WEIGHTS,
  indexScores,
  resolveMilestoneScore,
  resolveThreadScore,
  computePriority,
} from '../../../api/_priorityModel.js'

async function post(body) {
  const token = (typeof window !== 'undefined' && window.__clerkGetToken) ? await window.__clerkGetToken() : null
  const res = await fetch('/api/priority', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify(body),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json.error || ('Request failed: ' + res.status))
  return json
}

/**
 * Everything curated so far: `{ scores, dials, stats }`.
 * `stats.neverReviewed` counts rows no human has confirmed — the staleness signal §7 requires be
 * shown distinctly from an old-but-real review date.
 */
export function getPriorityInputs() {
  return post({ action: 'list' })
}

/** Upsert one score. Pass null for either half to leave it unscored. Stamps reviewed_at. */
export function setPriorityScore(scopeKind, scopeKey, { costOfDelay, effort, note } = {}) {
  return post({ action: 'setScore', scopeKind, scopeKey, costOfDelay, effort, note })
}

/** Remove a score entirely — resolution falls back to the next scope up (or the old ranking). */
export function clearPriorityScore(scopeKind, scopeKey) {
  return post({ action: 'clearScore', scopeKind, scopeKey })
}

/**
 * "Looked at it, still right." Restamps reviewed_at without changing values — the only honest way
 * to clear staleness when nothing needed to change. Without this, confirming no-change is
 * impossible and the staleness flag becomes permanent noise (the Progress Gate hit exactly this).
 */
export function confirmPriorityScore(scopeKind, scopeKey) {
  return post({ action: 'touchScore', scopeKind, scopeKey })
}

/** Set a project's dial. Clearing it (below) is how you get back to the implicit 1.0. */
export function setProjectDial(projectId, dial, reason) {
  return post({ action: 'setProjectDial', projectId, dial, reason })
}

export function clearProjectDial(projectId) {
  return post({ action: 'clearProjectDial', projectId })
}

/* ── Phase 2: the shadow ranking ─────────────────────────────────────────────────────────────── */

/**
 * The side-by-side comparison — today's ranking against the new one, for the exact population the
 * nightly scheduler ranks.
 *
 * **SHADOW MODE. Nothing consumes this and nothing writes a date from it.** It exists so the model
 * can be validated while being wrong is still free (docs/PRIORITY_ENGINE.md Phase 2). The first
 * real consumer is Phase 4.
 *
 * Computed entirely server-side — it needs several Airtable round trips plus the whole scheduling
 * mirror, which is one call from a Vercel function and would be a rate-limited fan-out through the
 * browser's queued proxy. Expect several seconds; show a loading state.
 *
 * Returns `{ generatedAt, weights, rows, stats, warnings }`. See api/priority.js#preview.
 */
export function getPriorityPreview() {
  return post({ action: 'preview' })
}
